import { Routes } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Pacientes } from './pacientes/pacientes';
import { PacientesList } from '@pacientes/components/pacientes-list/pacientes-list';
import { LoginComponent } from './auth/components/login/login.component';
import { authGuard } from './auth/guards/auth.guard';
import { recursoGuard } from './auth/guards/recurso.guard';
import { RECURSOS, RECURSOS_PANEL_ALERTAS, RECURSOS_VIOLACIONES_ALERTAS } from '@auth/services/permission.service';
import { CONFIGURACION_ROUTES } from '@configuracion/configuracion.routes';
import { composerUnsavedChangesGuard } from '@composer/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'home',
    loadComponent: () => import('@home/home').then(m => m.Home),
    canActivate: [authGuard],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/home',
  },
  {
    path: 'pacientes',
    component: Pacientes,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: PacientesList,
      },
      {
        path: ':form_id',
        loadComponent: () => import('@pacientes/components/sh-app-form/sh-app-form').then(m => m.ShAppForm),
        data: { redirectTo: '/pacientes', validatePatient: true },
      },
      // {
      //   path: ':component_location',
      //   component: CrearPacienteTBS,
      // },
      // {
      //   path: 'crear-tbf',
      //   component: CrearPacienteTBS,
      // },
      // {
      //   path: 'crear-qmo',
      //   component: CrearPacienteTBS,
      // },
      // {
      //   path: 'crear-lpa',
      //   component: CrearPacienteTBS,
      // },
    ],
  },
  {
    path: 'sintomaticos-respiratorios',
    loadChildren: () =>
      import('@sintomaticos-respiratorios/sintomaticos-respiratorios.routes').then(
        m => m.SINTOMATICOS_RESPIRATORIOS_ROUTES,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'ver-paciente/:caso_id',
    loadChildren: () => import('@ver-paciente/ver-paciente.routes').then(m => m.VER_PACIENTE_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'editar-paciente/:caso_id',
    loadComponent: () => import('@pacientes/components/sh-app-form/sh-app-form').then(m => m.ShAppForm),
    canActivate: [authGuard],
    data: { validatePatient: true },
  },
  {
    path: 'editar-ingreso-paciente',
    loadChildren: () =>
      import('@editar-ingreso-paciente/editar-ingreso-paciente.routes').then(m => m.EDITAR_INGRESO_ROUTES),
    canActivate: [authGuard],
  },
  // "Ver / Editar ingreso de paciente" (ingreso-paciente-plan.md §5.4): read-only
  // sheet of a caso's intake forms, with an in-page switch to edit one at a time.
  {
    path: 'ingreso-paciente/:component_location/:caso_id',
    loadChildren: () => import('@ingreso-paciente/ingreso-paciente.routes').then(m => m.INGRESO_PACIENTE_ROUTES),
    canActivate: [authGuard],
  },
  // Same page, keyed by contacto_id instead of caso_id (CP0,
  // ingreso-paciente-tratamientos-plan.md "Shared design" §2) -- same lazy
  // routes file (guards, canDeactivate, ConfirmationService provider), only
  // the route param differs.
  {
    path: 'ingreso-contacto/:component_location/:contacto_id',
    loadChildren: () => import('@ingreso-paciente/ingreso-paciente.routes').then(m => m.INGRESO_PACIENTE_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'tratamiento/quimioprofilaxis',
    loadChildren: () =>
      import('@tratamiento-qmo/tratamiento-qmo.routes').then(m => m.TRATAMIENTO_QMO_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'tratamiento/tuberculosis_sensible',
    loadChildren: () => import('@tratamiento-tbs/tratamiento-tbs.routes').then(m => m.TRATAMIENTO_TBS_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'tratamiento/tuberculosis_farmacorresistente',
    loadChildren: () => import('@tratamiento-tbf/tratamiento-tbf.routes').then(m => m.TRATAMIENTO_TBF_ROUTES),
    canActivate: [authGuard],
  },
  // Módulo de Lepra (ver ui-v3/src/app/tratamiento-lepra/README.md): dos destinos con árbol
  // lateral propio, cada uno gateado por el recurso que habilita su formulario de escritura.
  {
    path: 'tratamiento/lepra',
    loadChildren: () => import('@tratamiento-lepra/tratamiento-lepra.routes').then(m => m.TRATAMIENTO_LEPRA_ROUTES),
    canActivate: [authGuard, recursoGuard(RECURSOS.INGRESO_TRATAMIENTO_LPA)],
  },
  {
    path: 'egreso/lepra',
    loadChildren: () => import('@tratamiento-lepra/tratamiento-lepra.routes').then(m => m.EGRESO_LEPRA_ROUTES),
    canActivate: [authGuard, recursoGuard(RECURSOS.INGRESO_EGRESO_LPA)],
  },
  {
    path: 'contactos-tbs',
    loadChildren: () => import('@contactos-tbs/contactos-tbs.routes').then(m => m.CONTACTOS_TBS_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'contactos-tbf',
    loadChildren: () => import('./contactos-tbf/contactos-tbf.routes').then(m => m.CONTACTOS_TBF_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'contactos',
    loadComponent: () => import('./contactos/contactos').then(m => m.Contactos),
    canActivate: [authGuard],
  },
  {
    path: 'contactos/:persona_id',
    loadComponent: () => import('@pacientes/components/sh-app-form/sh-app-form').then(m => m.ShAppForm),
    canActivate: [authGuard],
    data: { validatePatient: true },
  },
  {
    path: 'convivientes/:persona_id',
    loadComponent: () => import('@pacientes/components/sh-app-form/sh-app-form').then(m => m.ShAppForm),
    canActivate: [authGuard],
  },
  {
    path: 'egreso-tbs/:caso_id',
    loadComponent: () => import('@egreso-tbs/components/egreso-tbs/egreso-tbs').then(m => m.EgresoTbs),
    canActivate: [authGuard],
  },
  {
    path: 'egreso-tbf/:caso_id',
    loadComponent: () => import('@egreso-tbf/components/egreso-tbf/egreso-tbf').then(m => m.EgresoTbf),
    canActivate: [authGuard],
  },
  {
    path: 'egreso-qmo/:caso_id',
    loadComponent: () => import('@egreso-qmo/components/egreso-qmo/egreso-qmo').then(m => m.EgresoQmo),
    canActivate: [authGuard, recursoGuard(RECURSOS.INGRESO_EGRESO_QMO)],
  },
  {
    path: 'georreferenciacion/:persona_id',
    loadComponent: () =>
      import('@georreferenciacion/components/georreferenciacion/georreferenciacion').then(
        m => m.Georreferenciacion,
      ),
    canActivate: [authGuard, recursoGuard(RECURSOS.INGRESO_GEORREFERENCIACION)],
  },
  {
    path: 'control-de-acceso/:userId',
    loadComponent: () =>
      import('./control-de-acceso/control-de-acceso').then(m => m.ControlDeAcceso),
    canActivate: [authGuard, recursoGuard(RECURSOS.GESTIONAR_ACCESOS)],
  },
  {
    path: 'reportes',
    loadChildren: () => import('@reportes/reportes.routes').then(m => m.REPORTES_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'sivigila',
    loadChildren: () => import('@sivigila/sivigila.routes').then(m => m.SIVIGILA_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'configuracion',
    // P06 (Permisos granulares, D4): quien solo tiene alguno de los 4
    // recursos del panel de alertas (sin sh_ver_configuracion) también entra
    // -- config-page.html/configuracion.routes.ts deciden qué ve adentro.
    canActivate: [authGuard, recursoGuard([RECURSOS.VER_CONFIGURACION, ...RECURSOS_PANEL_ALERTAS])],
    children: CONFIGURACION_ROUTES,
  },
  // P06 (Permisos granulares, D7): fuera de /configuracion a propósito — un
  // asignado con solo sh_ver_violaciones_alertas (sin sh_ver_configuracion)
  // abre el detalle de su violación desde la notificación del popover.
  {
    path: 'alertas',
    loadChildren: () => import('@alertas/alertas.routes').then(m => m.ALERTAS_ROUTES),
    canActivate: [authGuard, recursoGuard([...RECURSOS_VIOLACIONES_ALERTAS])],
  },
  {
    path: 'crear-formulario/:component_location',
    loadComponent: () =>
      import('@configuracion/components/crear-formulario/crear-formulario').then(m => m.CrearFormulario),
    canActivate: [authGuard, recursoGuard(RECURSOS.VER_CONFIGURACION)],
  },
  {
    path: 'crear-tabla/:component_location',
    loadComponent: () => import('@configuracion/components/crear-tabla/crear-tabla').then(m => m.CrearTabla),
    canActivate: [authGuard, recursoGuard(RECURSOS.VER_CONFIGURACION)],
  },
  {
    path: 'composer',
    // WP4 (Alertas y Notificaciones): a publisher who only holds PUBLICAR_FORMULARIO
    // (not VER_CONFIGURACION) can still open a `/composer/form/{formId}` deep link from
    // an alerta -- the composer route no longer requires the configuration recurso on
    // its own, either recurso is enough. The route lets them in read-only (or to
    // publish); FormEditor's own gating still controls what they can DO once inside.
    canActivate: [authGuard, recursoGuard([RECURSOS.VER_CONFIGURACION, RECURSOS.PUBLICAR_FORMULARIO])],
    loadComponent: () => import('@composer/composer-page/composer-page').then(m => m.ComposerPage),
    loadChildren: () => [
      {
        path: 'form/:formId',
        loadComponent: () => import('@composer/components/form-editor/form-editor').then(m => m.FormEditor),
        // `ConfirmationService` here is shared by `composerUnsavedChangesGuard` and
        // FormEditor's own `<p-confirmdialog>` -- both resolve it from this route's
        // injector, so they end up talking to the same instance.
        providers: [ConfirmationService],
        canDeactivate: [composerUnsavedChangesGuard],
      },
      {
        path: 'form/agregar-campo/:formId',
        loadComponent: () =>
          import('@composer/components/add-form-field/add-form-field').then(m => m.AddFormField),
      },
      {
        path: 'tabla/:grid_id',
        loadComponent: () =>
          import('@configuracion/components/grid-composer/grid-composer').then(m => m.GridComposer),
      },
    ],
    // children: [
    //   {
    //     path: 'form/:formId',
    //     loadComponent: () => import('@configuracion/components/composer/composer').then(m => m.Composer),
    //   },
    // ],
  },
  // {
  //   path: 'composer',
  //   children: [
  //     {
  //       path: ':location',
  //       loadComponent: () => import('@configuracion/components/composer/composer').then(m => m.Composer),
  //       children: [
  //         {
  //           path: '',
  //           redirectTo: 'forms',
  //           pathMatch: 'full',
  //         },
  //         {
  //           path: 'forms',
  //           loadComponent: () => import('@configuracion/components/composer/forms/forms').then(m => m.Forms),
  //         },
  //       ],
  //     },
  //   ],
  // },
];
